/**
 * Google Gemini Setup Tutorial Page
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { localizePath } from '@/lib/locale-path';
import { useDocumentMeta } from '@/lib/document-meta';
import './tutorials.scss';

interface Translation {
  pageTitle: string;
  backLink: string;
  overview: { title: string; content: string };
  steps: { title: string; content: string }[];
  tips: { title: string; items: string[] };
  troubleshooting: { title: string; content: string };
}

const translations: Record<Locale, Translation> = {
  en: {
    pageTitle: 'Google Gemini Setup Tutorial',
    backLink: 'Back to AI Providers',
    overview: {
      title: 'Overview',
      content: 'This guide will help you set up Google Gemini for use with Sokuji.',
    },
    steps: [
      { title: 'Create a Google Cloud Account', content: 'Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> and create an account or sign in with your existing Google account.' },
      { title: 'Enable the Gemini API', content: 'Navigate to the API Library and search for "Gemini API" or "Generative Language API". Click Enable to activate it for your project.' },
      { title: 'Create API Credentials', content: 'Go to APIs & Services > Credentials. Click "Create Credentials" and select "API Key". Copy the generated key and store it securely.' },
      { title: 'Configure in Sokuji', content: 'Open Sokuji settings, select "Google Gemini" as your AI provider, and paste your API key. Test the connection to ensure it works.' },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Set up billing alerts to monitor your usage',
        'Use the Gemini 1.5 Flash model for faster responses',
        'Consider using Gemini 1.5 Pro for higher quality translations',
        'Keep your API key secure and rotate it periodically',
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter issues, verify your API key is correct, ensure billing is enabled on your Google Cloud account, and check that the Gemini API is enabled. Visit <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">Google AI Documentation</a> for more help.',
    },
  },
  zh: {
    pageTitle: 'Google Gemini 设置教程',
    backLink: '返回 AI 提供商',
    overview: {
      title: '概述',
      content: '本指南将帮助您设置 Google Gemini 以与 Sokuji 配合使用。Google Gemini 提供具有竞争力价格的强大 AI 模型，用于实时翻译。',
    },
    steps: [
      { title: '创建 Google Cloud 账户', content: '访问 <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> 并创建账户或使用现有的 Google 账户登录。' },
      { title: '启用 Gemini API', content: '导航到 API 库并搜索 "Gemini API" 或 "Generative Language API"。点击启用以为您的项目激活它。' },
      { title: '创建 API 凭据', content: '转到 APIs & Services > 凭据。点击"创建凭据"并选择"API 密钥"。复制生成的密钥并安全存储。' },
      { title: '在 Sokuji 中配置', content: '打开 Sokuji 设置，选择"Google Gemini"作为您的 AI 提供商，并粘贴您的 API 密钥。测试连接以确保正常工作。' },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '设置账单提醒以监控您的使用情况',
        '使用 Gemini 1.5 Flash 模型获得更快的响应',
        '考虑使用 Gemini 1.5 Pro 获得更高质量的翻译',
        '保护您的 API 密钥安全并定期轮换',
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到问题，请验证您的 API 密钥是否正确，确保 Google Cloud 账户已启用计费，并检查 Gemini API 是否已启用。访问 <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">Google AI 文档</a> 获取更多帮助。',
    },
  },
  ja: {
    pageTitle: 'Google Gemini セットアップチュートリアル',
    backLink: 'AI プロバイダーに戻る',
    overview: {
      title: '概要',
      content: 'このガイドでは、Sokuji で使用するための Google Gemini のセットアップを説明します。Google Gemini は、リアルタイム翻訳のための競争力のある価格で強力な AI モデルを提供しています。',
    },
    steps: [
      { title: 'Google Cloud アカウントを作成', content: '<a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> にアクセスしてアカウントを作成するか、既存の Google アカウントでサインインします。' },
      { title: 'Gemini API を有効化', content: 'API ライブラリに移動し、「Gemini API」または「Generative Language API」を検索します。有効化をクリックしてプロジェクトでアクティブにします。' },
      { title: 'API 認証情報を作成', content: 'APIs & Services > 認証情報に移動します。「認証情報を作成」をクリックし、「API キー」を選択します。生成されたキーをコピーして安全に保存します。' },
      { title: 'Sokuji で設定', content: 'Sokuji の設定を開き、AI プロバイダーとして「Google Gemini」を選択し、API キーを貼り付けます。接続をテストして正常に動作することを確認します。' },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        '使用状況を監視するために請求アラートを設定する',
        'より高速な応答のために Gemini 1.5 Flash モデルを使用する',
        'より高品質な翻訳のために Gemini 1.5 Pro の使用を検討する',
        'API キーを安全に保ち、定期的にローテーションする',
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、API キーが正しいか確認し、Google Cloud アカウントで請求が有効になっていることを確認し、Gemini API が有効になっていることを確認してください。詳細は <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">Google AI ドキュメント</a> を参照してください。',
    },
  },
  ko: {
    pageTitle: 'Google Gemini 설정 튜토리얼',
    backLink: 'AI 제공업체로 돌아가기',
    overview: {
      title: '개요',
      content: '이 가이드는 Sokuji와 함께 사용하기 위한 Google Gemini 설정을 도와드립니다. Google Gemini는 실시간 번역을 위한 경쟁력 있는 가격의 강력한 AI 모델을 제공합니다.',
    },
    steps: [
      { title: 'Google Cloud 계정 만들기', content: '<a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a>에 접속하여 계정을 만들거나 기존 Google 계정으로 로그인하세요.' },
      { title: 'Gemini API 활성화', content: 'API 라이브러리로 이동하여 "Gemini API" 또는 "Generative Language API"를 검색합니다. 활성화를 클릭하여 프로젝트에서 활성화합니다.' },
      { title: 'API 자격 증명 만들기', content: 'APIs & Services > 자격 증명으로 이동합니다. "자격 증명 만들기"를 클릭하고 "API 키"를 선택합니다. 생성된 키를 복사하여 안전하게 저장합니다.' },
      { title: 'Sokuji에서 구성', content: 'Sokuji 설정을 열고 AI 제공업체로 "Google Gemini"를 선택하고 API 키를 붙여넣습니다. 연결을 테스트하여 정상 작동하는지 확인합니다.' },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '사용량을 모니터링하기 위해 결제 알림 설정',
        '더 빠른 응답을 위해 Gemini 1.5 Flash 모델 사용',
        '더 높은 품질의 번역을 위해 Gemini 1.5 Pro 사용 고려',
        'API 키를 안전하게 보관하고 주기적으로 교체',
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 API 키가 올바른지 확인하고, Google Cloud 계정에서 결제가 활성화되어 있는지 확인하고, Gemini API가 활성화되어 있는지 확인하세요. 자세한 내용은 <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">Google AI 문서</a>를 참조하세요.',
    },
  },
  es: {
    pageTitle: 'Tutorial de configuración de Google Gemini',
    backLink: 'Volver a proveedores de IA',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le ayudará a configurar Google Gemini para usarlo con Sokuji. Google Gemini ofrece potentes modelos de IA con precios competitivos para la traducción en tiempo real.',
    },
    steps: [
      { title: 'Crear una cuenta de Google Cloud', content: 'Vaya a <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> y cree una cuenta o inicie sesión con su cuenta de Google existente.' },
      { title: 'Habilitar la API de Gemini', content: 'Navegue a la biblioteca de APIs y busque "Gemini API" o "Generative Language API". Haga clic en Habilitar para activarla en su proyecto.' },
      { title: 'Crear credenciales de API', content: 'Vaya a APIs & Services > Credenciales. Haga clic en "Crear credenciales" y seleccione "Clave de API". Copie la clave generada y guárdela de forma segura.' },
      { title: 'Configurar en Sokuji', content: 'Abra la configuración de Sokuji, seleccione "Google Gemini" como su proveedor de IA y pegue su clave de API. Pruebe la conexión para asegurarse de que funcione.' },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Configure alertas de facturación para supervisar su uso',
        'Use el modelo Gemini 1.5 Flash para respuestas más rápidas',
        'Considere usar Gemini 1.5 Pro para traducciones de mayor calidad',
        'Mantenga su clave de API segura y rótela periódicamente',
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra problemas, verifique que su clave de API sea correcta, asegúrese de que la facturación esté habilitada en su cuenta de Google Cloud y compruebe que la API de Gemini esté habilitada. Visite la <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">documentación de Google AI</a> para obtener más ayuda.',
    },
  },
  fr: {
    pageTitle: 'Tutoriel de configuration Google Gemini',
    backLink: 'Retour aux fournisseurs d\'IA',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous aidera à configurer Google Gemini pour l\'utiliser avec Sokuji. Google Gemini fournit des modèles d\'IA puissants à des prix compétitifs pour la traduction en temps réel.',
    },
    steps: [
      { title: 'Créer un compte Google Cloud', content: 'Rendez-vous sur <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> et créez un compte ou connectez-vous avec votre compte Google existant.' },
      { title: 'Activer l\'API Gemini', content: 'Accédez à la bibliothèque d\'API et recherchez « Gemini API » ou « Generative Language API ». Cliquez sur Activer pour l\'activer dans votre projet.' },
      { title: 'Créer des identifiants API', content: 'Allez dans APIs & Services > Identifiants. Cliquez sur « Créer des identifiants » et sélectionnez « Clé API ». Copiez la clé générée et conservez-la en lieu sûr.' },
      { title: 'Configurer dans Sokuji', content: 'Ouvrez les paramètres de Sokuji, sélectionnez « Google Gemini » comme fournisseur d\'IA et collez votre clé API. Testez la connexion pour vous assurer qu\'elle fonctionne.' },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Configurez des alertes de facturation pour surveiller votre utilisation',
        'Utilisez le modèle Gemini 1.5 Flash pour des réponses plus rapides',
        'Envisagez d\'utiliser Gemini 1.5 Pro pour des traductions de meilleure qualité',
        'Gardez votre clé API en sécurité et faites-la tourner périodiquement',
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, vérifiez que votre clé API est correcte, assurez-vous que la facturation est activée sur votre compte Google Cloud et vérifiez que l\'API Gemini est activée. Consultez la <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">documentation Google AI</a> pour plus d\'aide.',
    },
  },
  ar: {
    pageTitle: 'دليل إعداد Google Gemini',
    backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
    overview: {
      title: 'نظرة عامة',
      content: 'سيساعدك هذا الدليل على إعداد Google Gemini للاستخدام مع Sokuji. يوفر Google Gemini نماذج ذكاء اصطناعي قوية بأسعار تنافسية للترجمة في الوقت الفعلي.',
    },
    steps: [
      { title: 'إنشاء حساب Google Cloud', content: 'انتقل إلى <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> وأنشئ حسابًا أو سجّل الدخول بحساب Google الحالي.' },
      { title: 'تفعيل Gemini API', content: 'انتقل إلى مكتبة API وابحث عن "Gemini API" أو "Generative Language API". انقر على تفعيل لتنشيطها في مشروعك.' },
      { title: 'إنشاء بيانات اعتماد API', content: 'انتقل إلى APIs & Services > بيانات الاعتماد. انقر على "إنشاء بيانات اعتماد" وحدد "مفتاح API". انسخ المفتاح المُنشأ واحفظه بشكل آمن.' },
      { title: 'التكوين في Sokuji', content: 'افتح إعدادات Sokuji، وحدد "Google Gemini" كمزود الذكاء الاصطناعي والصق مفتاح API الخاص بك. اختبر الاتصال للتأكد من أنه يعمل.' },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'قم بإعداد تنبيهات الفواتير لمراقبة استخدامك',
        'استخدم نموذج Gemini 1.5 Flash للحصول على استجابات أسرع',
        'فكر في استخدام Gemini 1.5 Pro للحصول على ترجمات أعلى جودة',
        'حافظ على أمان مفتاح API الخاص بك وقم بتدويره بشكل دوري',
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت مشكلات، تحقق من صحة مفتاح API الخاص بك، وتأكد من تفعيل الفواتير في حساب Google Cloud الخاص بك، وتحقق من تفعيل Gemini API. قم بزيارة <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">وثائق Google AI</a> للحصول على مزيد من المساعدة.',
    },
  },
  pt: {
    pageTitle: 'Tutorial de configuração do Google Gemini',
    backLink: 'Voltar para provedores de IA',
    overview: {
      title: 'Visão geral',
      content: 'Este guia ajudará você a configurar o Google Gemini para uso com o Sokuji. O Google Gemini oferece modelos de IA poderosos com preços competitivos para tradução em tempo real.',
    },
    steps: [
      { title: 'Criar uma conta Google Cloud', content: 'Acesse o <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> e crie uma conta ou faça login com sua conta Google existente.' },
      { title: 'Ativar a API Gemini', content: 'Navegue até a biblioteca de APIs e pesquise "Gemini API" ou "Generative Language API". Clique em Ativar para ativá-la no seu projeto.' },
      { title: 'Criar credenciais de API', content: 'Vá para APIs & Services > Credenciais. Clique em "Criar credenciais" e selecione "Chave de API". Copie a chave gerada e armazene-a com segurança.' },
      { title: 'Configurar no Sokuji', content: 'Abra as configurações do Sokuji, selecione "Google Gemini" como seu provedor de IA e cole sua chave de API. Teste a conexão para garantir que funcione.' },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Configure alertas de faturamento para monitorar seu uso',
        'Use o modelo Gemini 1.5 Flash para respostas mais rápidas',
        'Considere usar o Gemini 1.5 Pro para traduções de maior qualidade',
        'Mantenha sua chave de API segura e faça a rotação periodicamente',
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar problemas, verifique se sua chave de API está correta, certifique-se de que o faturamento está habilitado em sua conta Google Cloud e verifique se a API Gemini está ativada. Visite a <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">documentação do Google AI</a> para mais ajuda.',
    },
  },
  ru: {
    pageTitle: 'Руководство по настройке Google Gemini',
    backLink: 'Назад к провайдерам ИИ',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить Google Gemini для использования с Sokuji. Google Gemini предоставляет мощные модели ИИ по конкурентным ценам для перевода в реальном времени.',
    },
    steps: [
      { title: 'Создайте аккаунт Google Cloud', content: 'Перейдите в <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> и создайте аккаунт или войдите с помощью существующего аккаунта Google.' },
      { title: 'Включите Gemini API', content: 'Перейдите в библиотеку API и найдите «Gemini API» или «Generative Language API». Нажмите «Включить», чтобы активировать его для вашего проекта.' },
      { title: 'Создайте учётные данные API', content: 'Перейдите в APIs & Services > Учётные данные. Нажмите «Создать учётные данные» и выберите «Ключ API». Скопируйте сгенерированный ключ и сохраните его в безопасном месте.' },
      { title: 'Настройте в Sokuji', content: 'Откройте настройки Sokuji, выберите «Google Gemini» в качестве провайдера ИИ и вставьте ваш ключ API. Проверьте подключение, чтобы убедиться, что оно работает.' },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Настройте оповещения о расходах для отслеживания использования',
        'Используйте модель Gemini 1.5 Flash для более быстрых ответов',
        'Рассмотрите использование Gemini 1.5 Pro для более качественных переводов',
        'Храните API-ключ в безопасности и периодически обновляйте его',
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'При возникновении проблем проверьте правильность API-ключа, убедитесь, что биллинг включён в вашем аккаунте Google Cloud, и проверьте, что Gemini API активирован. Посетите <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">документацию Google AI</a> для получения дополнительной помощи.',
    },
  },
  de: {
    pageTitle: 'Google Gemini-Einrichtungstutorial',
    backLink: 'Zurück zu KI-Anbietern',
    overview: {
      title: 'Übersicht',
      content: 'Diese Anleitung hilft Ihnen bei der Einrichtung von Google Gemini für die Verwendung mit Sokuji. Google Gemini bietet leistungsstarke KI-Modelle zu wettbewerbsfähigen Preisen für Echtzeit-Übersetzung.',
    },
    steps: [
      { title: 'Google Cloud-Konto erstellen', content: 'Gehen Sie zur <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> und erstellen Sie ein Konto oder melden Sie sich mit Ihrem bestehenden Google-Konto an.' },
      { title: 'Gemini API aktivieren', content: 'Navigieren Sie zur API-Bibliothek und suchen Sie nach „Gemini API" oder „Generative Language API". Klicken Sie auf Aktivieren, um sie für Ihr Projekt zu aktivieren.' },
      { title: 'API-Anmeldeinformationen erstellen', content: 'Gehen Sie zu APIs & Services > Anmeldeinformationen. Klicken Sie auf „Anmeldeinformationen erstellen" und wählen Sie „API-Schlüssel". Kopieren Sie den generierten Schlüssel und speichern Sie ihn sicher.' },
      { title: 'In Sokuji konfigurieren', content: 'Öffnen Sie die Sokuji-Einstellungen, wählen Sie „Google Gemini" als Ihren KI-Anbieter und fügen Sie Ihren API-Schlüssel ein. Testen Sie die Verbindung, um sicherzustellen, dass sie funktioniert.' },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Richten Sie Abrechnungsbenachrichtigungen ein, um Ihre Nutzung zu überwachen',
        'Verwenden Sie das Gemini 1.5 Flash-Modell für schnellere Antworten',
        'Erwägen Sie die Verwendung von Gemini 1.5 Pro für Übersetzungen höherer Qualität',
        'Halten Sie Ihren API-Schlüssel sicher und erneuern Sie ihn regelmäßig',
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Probleme auftreten, überprüfen Sie, ob Ihr API-Schlüssel korrekt ist, stellen Sie sicher, dass die Abrechnung in Ihrem Google Cloud-Konto aktiviert ist, und prüfen Sie, ob die Gemini API aktiviert ist. Besuchen Sie die <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">Google AI-Dokumentation</a> für weitere Hilfe.',
    },
  },
  uk: {
    pageTitle: 'Посібник з налаштування Google Gemini',
    backLink: 'Назад до провайдерів ШІ',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати Google Gemini для використання з Sokuji. Google Gemini надає потужні моделі ШІ за конкурентними цінами для перекладу в реальному часі.',
    },
    steps: [
      { title: 'Створіть обліковий запис Google Cloud', content: 'Перейдіть до <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> та створіть обліковий запис або увійдіть за допомогою наявного облікового запису Google.' },
      { title: 'Увімкніть Gemini API', content: 'Перейдіть до бібліотеки API та знайдіть «Gemini API» або «Generative Language API». Натисніть «Увімкнути», щоб активувати його для вашого проекту.' },
      { title: 'Створіть облікові дані API', content: 'Перейдіть до APIs & Services > Облікові дані. Натисніть «Створити облікові дані» та виберіть «Ключ API». Скопіюйте згенерований ключ та збережіть його в безпечному місці.' },
      { title: 'Налаштуйте в Sokuji', content: 'Відкрийте налаштування Sokuji, виберіть «Google Gemini» як провайдера ШІ та вставте ваш ключ API. Перевірте підключення, щоб переконатися, що воно працює.' },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Налаштуйте сповіщення про оплату для моніторингу використання',
        'Використовуйте модель Gemini 1.5 Flash для швидших відповідей',
        'Розгляньте використання Gemini 1.5 Pro для перекладів вищої якості',
        'Зберігайте ваш ключ API в безпеці та періодично оновлюйте його',
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо виникли проблеми, перевірте правильність вашого ключа API, переконайтеся, що білінг увімкнено у вашому обліковому записі Google Cloud, та перевірте, що Gemini API активовано. Відвідайте <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">документацію Google AI</a> для отримання додаткової допомоги.',
    },
  },
  it: {
    pageTitle: 'Tutorial di configurazione Google Gemini',
    backLink: 'Torna ai fornitori di IA',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti aiuterà a configurare Google Gemini per l\'uso con Sokuji. Google Gemini offre potenti modelli di IA a prezzi competitivi per la traduzione in tempo reale.',
    },
    steps: [
      { title: 'Creare un account Google Cloud', content: 'Vai alla <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> e crea un account o accedi con il tuo account Google esistente.' },
      { title: 'Attivare l\'API Gemini', content: 'Naviga nella libreria API e cerca "Gemini API" o "Generative Language API". Clicca su Attiva per attivarla nel tuo progetto.' },
      { title: 'Creare credenziali API', content: 'Vai su APIs & Services > Credenziali. Clicca su "Crea credenziali" e seleziona "Chiave API". Copia la chiave generata e conservala in modo sicuro.' },
      { title: 'Configurare in Sokuji', content: 'Apri le impostazioni di Sokuji, seleziona "Google Gemini" come fornitore di IA e incolla la tua chiave API. Testa la connessione per assicurarti che funzioni.' },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Configura avvisi di fatturazione per monitorare il tuo utilizzo',
        'Usa il modello Gemini 1.5 Flash per risposte più veloci',
        'Considera l\'uso di Gemini 1.5 Pro per traduzioni di qualità superiore',
        'Mantieni la tua chiave API al sicuro e ruotala periodicamente',
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, verifica che la tua chiave API sia corretta, assicurati che la fatturazione sia abilitata nel tuo account Google Cloud e controlla che l\'API Gemini sia attivata. Visita la <a href="https://ai.google.dev/docs" target="_blank" rel="noopener noreferrer">documentazione Google AI</a> per ulteriore assistenza.',
    },
  },
};

export function GeminiSetup() {
  const { locale } = useI18n();
  const t = translations[locale] || translations.en;
  useDocumentMeta(t.pageTitle, t.overview.content);

  return (
    <div className="docs-content tutorial-page">
      <Link to={localizePath('/docs/ai-providers', locale)} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <section className="tutorial-page__section">
        <h2>{t.overview.title}</h2>
        <p>{t.overview.content}</p>
      </section>

      <section className="tutorial-page__section">
        {t.steps.map((step, index) => (
          <div key={index} className="tutorial-page__step">
            <h3>
              <span className="tutorial-page__step-number">{index + 1}</span>
              {step.title}
            </h3>
            <p dangerouslySetInnerHTML={{ __html: step.content }} />
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <div className="tutorial-page__tip">
          <h4>{t.tips.title}</h4>
          <ul>
            {t.tips.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="tutorial-page__section">
        <h2>{t.troubleshooting.title}</h2>
        <p dangerouslySetInnerHTML={{ __html: t.troubleshooting.content }} />
      </section>

      <section className="tutorial-page__section">
        <a
          href="https://ai.google.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="install-page__download-btn"
        >
          Google AI for Developers
          <ExternalLink size={16} />
        </a>
      </section>
    </div>
  );
}
